import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import axios from "axios";
import { captureException } from "@sentry/react";

import { AWS_PROFILE_IMAGES_URL, BACKEND_SERVER_URL } from "constants";
import { DEFAULT_USERNAMES } from "constants/usernames";
import { sanitizedUsername } from "modules/functions";

import UpdateProfile from "./UpdateProfile";

export default () => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [avatarPosition, setAvatarPosition] = useState(100);
  const [picture, setPicture] = useState({
    cropperOpen: false,
    img: null,
    zoom: 2,
    error: null,
  });

  // create array of default avatars and save it to state
  const createDefaultAvatarsArray = () => {
    const arr = Array.from({ length: 200 }, (_, index) => index + 1001);

    const imagesArr = arr.map((item) => AWS_PROFILE_IMAGES_URL + item + ".svg");

    for (let i = imagesArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [imagesArr[i], imagesArr[j]] = [imagesArr[j], imagesArr[i]];
    }

    return imagesArr;
  };

  useEffect(() => {
    const avatarArray = createDefaultAvatarsArray();

    setAvatars(avatarArray);
  }, []);

  // Fetch data
  const GET_USER_PROFILE_DATA = gql`
    query GetUserProfileData {
      userProfileData {
        id
        username
        profile_image
      }
    }
  `;

  const {
    loading: loadingData,
    data,
    error,
  } = useQuery(GET_USER_PROFILE_DATA, {
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("fetching user data error", error);
    captureException(error);
  }

  //console.log("data", data);

  useEffect(() => {
    // set Avatar position to default profile picture
    if (data?.userProfileData.profile_image) {
      const defaultPosition = avatars.indexOf(
        AWS_PROFILE_IMAGES_URL + data.userProfileData.profile_image
      );

      if (defaultPosition !== -1) setAvatarPosition(defaultPosition);
      else {
        // add current profile image in the list of avatars
        let newAvatars = [
          ...avatars.slice(0, avatarPosition + 1),
          AWS_PROFILE_IMAGES_URL + data.userProfileData.profile_image,
          ...avatars.slice(avatarPosition + 1),
        ];

        setAvatars(newAvatars);
        setAvatarPosition(avatarPosition + 1);
      }
    }

    if (data?.userProfileData.username) {
      setUsername(data.userProfileData.username);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const sanitized = sanitizedUsername(e.target.value);

    setUsername(sanitized);
  };

  // handle username generation
  function getRandomItemFromArray(arr) {
    if (arr.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * arr.length);
    return arr[randomIndex];
  }

  const onGenerateClick = () => {
    const generatedUsername = getRandomItemFromArray(DEFAULT_USERNAMES);

    setUsername(generatedUsername);
  };

  // update username
  const UPDATE_USERNAME = gql`
    mutation UpdateUsername($username: String!) {
      updateUsername(username: $username) {
        id
        username
      }
    }
  `;

  const [updateUsername] = useMutation(UPDATE_USERNAME);

  const onFirstContinueClick = async () => {
    try {
      if (loadingUpdate) return;

      setStep(2);

      if (username !== "") {
        setLoadingUpdate(true);

        await updateUsername({
          variables: {
            username,
          },
        });

        setLoadingUpdate(false);
      }
    } catch (error) {
      captureException(error);
      console.log(error);
      setLoadingUpdate(false);

      return;
    }
  };

  const onBackClick = () => {
    setStep(1);
  };

  // handle Profile picture selection
  // browse through list of avatars
  const onMoveClick = (side) => {
    let newPosition = avatarPosition;

    if (side === "left") newPosition--;
    else newPosition++;

    if (newPosition > avatars.length - 1) newPosition = 0;
    if (newPosition < 0) newPosition = avatars.length - 1;

    setAvatarPosition(newPosition);
  };

  let editor = "";

  // handle Avatar editor
  const setEditorRef = (ed) => {
    editor = ed;
  };

  const handleUploadSlider = (event, value) => {
    setPicture({
      ...picture,
      zoom: value,
    });
  };

  const handleUploadCancel = () => {
    setPicture({
      ...picture,
      cropperOpen: false,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const fileNameParts = e.target.files[0].name.split(".");
      const fileExtension = fileNameParts[fileNameParts.length - 1];

      const allowedExtensions = ["jpg", "jpeg", "png", "gif"];

      if (!allowedExtensions.includes(fileExtension.toLowerCase())) {
        setPicture({
          ...picture,
          error: "Invalid file type!",
        });

        return;
      }

      let url = URL.createObjectURL(e.target.files[0]);

      setPicture({
        ...picture,
        img: url,
        cropperOpen: true,
      });
    }
  };

  // handle profile image cloud upload
  const axiosApi = axios.create({
    baseURL: BACKEND_SERVER_URL,
  });

  async function postFile(url, data, config = {}) {
    return axiosApi
      .post(url, data, { ...config })
      .then((response) => response.data);
  }

  function dataURItoBlob(dataURI) {
    var binary = atob(dataURI.split(",")[1]);
    var array = [];
    for (var i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: "image/png" });
  }

  const handleUploadSave = async (e) => {
    try {
      if (setEditorRef) {
        setLoadingUpdate(true);

        const canvasScaled = editor.getImageScaledToCanvas();
        const croppedImg = canvasScaled.toDataURL();

        setPicture({
          ...picture,
          img: null,
          cropperOpen: false,
        });

        var blob = dataURItoBlob(croppedImg);

        const formData = new FormData();
        formData.append("file", blob);

        const result = await postFile("/upload/profile-picture", formData, {
          headers: {
            "content-type": "multipart/form-data",
          },
        });

        let newAvatars = [
          ...avatars.slice(0, avatarPosition + 1),
          AWS_PROFILE_IMAGES_URL + result.fileName,
          ...avatars.slice(avatarPosition + 1),
        ];

        setAvatars(newAvatars);
        setAvatarPosition(avatarPosition + 1);

        setLoadingUpdate(false);
      }
    } catch (error) {
      captureException(error);
      console.log("handleUploadSave error", error);
    }
  };

  // update profile image
  const UPDATE_PROFILE_IMAGE = gql`
    mutation UpdateProfileImage($image: String!) {
      updateProfileImage(image: $image) {
        id
        profile_image
      }
    }
  `;

  const [updateProfileImage] = useMutation(UPDATE_PROFILE_IMAGE);

  const onSecondContinueClick = async () => {
    try {
      if (loadingUpdate) return;

      setLoadingUpdate(true);

      const parsedUrl = new URL(avatars[avatarPosition]);
      const filename = parsedUrl.pathname.slice(1);

      await updateProfileImage({
        variables: {
          image: filename,
        },
      });

      window.location = "/settings";
    } catch (error) {
      captureException(error);
      console.log(error);
      setLoadingUpdate(false);

      return;
    }
  };

  return (
    <UpdateProfile
      isOnboarding={false}
      step={step}
      username={username}
      handleInputChange={handleInputChange}
      defaultUsername={data?.userProfileData.username}
      loading={loadingData || loadingUpdate}
      onFirstContinueClick={onFirstContinueClick}
      onBackClick={onBackClick}
      onGenerateClick={onGenerateClick}
      profilePicture={avatars[avatarPosition]}
      onMoveClick={onMoveClick}
      cropperOpen={picture.cropperOpen}
      picture={picture}
      setEditorRef={setEditorRef}
      handleFileChange={handleFileChange}
      handleUploadSlider={handleUploadSlider}
      handleUploadCancel={handleUploadCancel}
      handleUploadSave={handleUploadSave}
      onSecondContinueClick={onSecondContinueClick}
    />
  );
};
